import { Link, Navigate, useParams } from "react-router-dom";
import {
  ArrowRight,
  BellRing,
  Building2,
  ClipboardCheck,
  Database,
  FileText,
  HeartPulse,
  LockKeyhole,
  Microscope,
  Stethoscope,
  Syringe,
  UserRoundCheck,
} from "lucide-react";
import { PatientInterestForm, PharmaContactForm, ProfessionalContactForm } from "@/components/marketing/PublicLeadForms";
import { ReferencesSection, ValueLevelsCompact, WhoDoesWhat } from "@/components/marketing/HomeStorySections";
import { PublicFooter, PublicHeader } from "@/components/marketing/PublicHeader";
import { useHashScroll, useSeo } from "@/components/marketing/MarketingPrimitives";
import ScrollToTopButton from "@/components/marketing/ScrollToTopButton";

const pageConfig = {
  patients: {
    label: "Patients",
    title: "Votre parcours continue chez vous. Votre équipe reste à vos côtés.",
    description: "Depuis votre téléphone : signalez vos symptômes, suivez votre traitement, envoyez vos examens, retrouvez vos rendez-vous. Votre infirmière voit ce qui change et prévient le médecin si nécessaire.",
    note: "HANA ne remplace ni votre médecin ni votre dossier médical ; il relie ce que vous vivez à l’équipe qui vous soigne.",
    seoTitle: "HANA pour les patients — votre parcours continue chez vous",
    seoDescription: "HANA permet aux patients suivis en oncologie de signaler leurs symptômes, suivre leur traitement oral, transmettre leurs examens et retrouver leurs rendez-vous, en lien avec l’équipe qui les soigne.",
    heroBadge: "Pour les patients",
    primaryCta: "Accéder à mon espace",
    ctaTo: "/login",
    secondaryCta: "Demander l’activation",
    secondaryTo: "#patient-activation-section",
    highlights: [
      [HeartPulse, "Signaler vos symptômes", "Fatigue, douleur, nausées, fièvre : quelques minutes suffisent pour que votre équipe soit informée."],
      [Syringe, "Suivre votre traitement oral", "Cochez vos prises, signalez un oubli ou un effet gênant : votre infirmière le voit le jour même."],
      [FileText, "Transmettre vos examens et documents", "Bilans, ordonnances, comptes rendus rejoignent votre parcours au lieu de se perdre."],
      [BellRing, "Retrouver vos rendez-vous et vos étapes", "Cures, consultations, examens : vous savez ce qui vient, et votre équipe aussi."],
    ],
    flow: ["Je signale ou je transmets", "Mon information rejoint mon parcours", "Mon infirmière vérifie et priorise", "Mon médecin décide si nécessaire"],
    closing: "Vos symptômes, vos documents et vos questions ne se perdent plus entre deux venues.",
  },
  professionnels: {
    label: "Établissements",
    title: "Coordonner le parcours de vos patients, sans alourdir vos équipes.",
    description: "Une infrastructure commune pour l’IDEC, les oncologues, le secrétariat et la direction. L’information structurée arrive au bon professionnel ; la décision reste médicale.",
    seoTitle: "HANA pour les établissements — coordination du parcours en oncologie",
    seoDescription: "HANA aide les établissements d’oncologie à coordonner le parcours de leurs patients : informations structurées, priorisation pour l’IDEC, bilan pré-chimio, traitement oral, synthèse du parcours, indicateurs pour la direction.",
    heroBadge: "Pour les établissements de santé",
    primaryCta: "Demander une démonstration",
    ctaTo: "#professional-contact-section",
    secondaryCta: "Voir le parcours",
    secondaryTo: "/#comment-ca-marche",
    highlights: [
      [UserRoundCheck, "IDEC : une vision structurée", "Une vue par patient et une liste du jour par équipe : qui rappeler, quel bilan vérifier, quel médecin prévenir."],
      [ClipboardCheck, "Bilan avant chaque cure", "Déposé, lu, vérifié par l’IDEC ; le médecin confirme ou reporte la cure ; secrétariat et patient informés."],
      [Stethoscope, "Médecin : décider, informé", "Il reçoit les situations qui demandent son avis, avec le contexte et l’historique. Chaque décision est tracée."],
      [Building2, "Direction : piloter", "Indicateurs de parcours et de coordination, base d’un pilotage et, si vous le souhaitez, de partenariats de données gouvernés."],
    ],
    flow: ["Le patient transmet", "HANA structure et priorise", "L’IDEC coordonne", "Le médecin décide"],
    closing: "Des informations dispersées à un parcours coordonné, tracé, et piloté par vos équipes.",
  },
  pharma: {
    label: "Partenaires & données",
    title: "Des données de vie réelle issues d’un parcours réellement coordonné.",
    description: "Parce que HANA structure le suivi quotidien, les établissements qui le souhaitent peuvent produire des indicateurs agrégés et gouvernés sur l’observance, les toxicités, les symptômes rapportés et les parcours. Le soin d’abord ; la donnée en conséquence.",
    seoTitle: "HANA — Partenaires & données de vie réelle en oncologie",
    seoDescription: "HANA permet aux établissements de produire, avec les partenaires de recherche et d’accès au marché, des indicateurs agrégés et gouvernés issus d’un parcours oncologique coordonné : observance, toxicités, symptômes rapportés, parcours.",
    heroBadge: "Pour les partenaires de recherche et d’accès au marché",
    primaryCta: "Échanger sur un projet",
    ctaTo: "#pharma-contact-section",
    secondaryCta: "Sécurité et données",
    secondaryTo: "/securite",
    highlights: [
      [Database, "Données issues du soin", "Produites par un parcours coordonné au quotidien, reliées au contexte clinique et aux étapes du parcours."],
      [Microscope, "Cohortes ciblées", "Par type de cancer, ligne de traitement, molécule, classe thérapeutique ou biomarqueur."],
      [Syringe, "Observance et toxicités", "Prises orales, oublis, symptômes et effets rapportés par les patients eux-mêmes."],
      [LockKeyhole, "Gouvernance", "Indicateurs agrégés, pseudonymisation forte avec techniques d’anonymisation, séparation stricte entre le soin et la lecture partenaire."],
    ],
    flow: ["Le patient est soigné et suivi", "Le parcours est structuré", "Le consentement recherche est recueilli", "Des indicateurs agrégés sont partagés"],
    closing: "Le soin d’abord ; la donnée est une conséquence, jamais une condition.",
  },
};

function SmartLink({ to, children, className, testId }) {
  if (to.startsWith("#")) return <a href={to} className={className} data-testid={testId}>{children}</a>;
  return <Link to={to} className={className} data-testid={testId}>{children}</Link>;
}

function AbstractJourney({ flow }) {
  return (
    <div className="rounded-[2rem] border border-emerald-100 bg-white p-6 shadow-[0_24px_70px_rgba(5,150,105,0.08)]" data-testid="audience-page-journey-card">
      <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-teal-500">Chaîne de coordination</p>
      <div className="mt-7 space-y-4">
        {flow.map((step, index) => (
          <div key={step} className="flex items-center gap-4 rounded-2xl bg-slate-50 p-4" data-testid={`audience-page-flow-step-${index + 1}`}>
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-sm font-extrabold text-white">{index + 1}</span>
            <span className="text-sm font-extrabold text-slate-800">{step}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProfessionalDepthSections() {
  return (
    <>
      <section className="px-5 pb-20 lg:px-8" data-testid="professional-whatsapp-section">
        <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-2">
          <div className="rounded-[2rem] border border-slate-200 bg-slate-50 p-8" data-testid="professional-before-card">
            <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-slate-500">Fragmentation</p>
            <h2 className="mt-4 text-3xl font-extrabold text-slate-800">Des messages partout, des bilans en pièce jointe, des urgences noyées.</h2>
            <p className="mt-5 leading-8 text-slate-500">Appels, messageries et pièces jointes ne trient rien, ne priorisent rien et ne gardent aucune trace exploitable. Chaque acteur ne voit qu’une partie du parcours.</p>
          </div>
          <div className="rounded-[2rem] border border-emerald-100 bg-emerald-50 p-8" data-testid="professional-after-card">
            <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-emerald-700">Continuité</p>
            <h2 className="mt-4 text-3xl font-extrabold text-slate-800">Un parcours structuré, une liste claire de ce qui demande une action aujourd’hui.</h2>
            <p className="mt-5 leading-8 text-slate-600">L’IDEC voit, vérifie, relance et prépare. Le médecin intervient sur les situations qui demandent réellement son avis. Chaque étape est tracée.</p>
          </div>
        </div>
      </section>

      <WhoDoesWhat testId="professional-responsibility-section" />

      <section className="px-5 pb-20 lg:px-8" data-testid="professional-deployment-section">
        <div className="mx-auto max-w-7xl">
          <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-teal-500">Déploiement</p>
          <h2 className="mt-4 max-w-3xl font-display text-3xl font-bold leading-tight text-slate-800 sm:text-4xl">Démarrer sans bouleverser votre organisation.</h2>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {[["1", "Choisir", "Par quoi commencer : bilans pré-chimio, symptômes, traitement oral ou documents."], ["2", "Démarrer", "Formation de l’équipe, création des comptes, premiers patients accompagnés."], ["3", "Ajuster", "Revue des alertes et des règles avec votre équipe, puis extension aux autres étapes du parcours."]].map(([num, title, text]) => (
              <article key={num} className="rounded-3xl border border-emerald-100 bg-white p-6 shadow-sm" data-testid={`professional-deployment-step-${num}`}>
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-600 font-extrabold text-white">{num}</span>
                <h3 className="mt-5 text-xl font-extrabold text-slate-800">{title}</h3>
                <p className="mt-3 text-sm leading-6 text-slate-500">{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function PharmaDepthSections() {
  const framework = ["Consentement distinct du soin", "Pseudonymisation forte avec techniques d’anonymisation", "Indicateurs agrégés, jamais de dossier individuel", "Comité de suivi et limites méthodologiques", "Conformité CNDP, finalités définies"];
  return (
    <>
      <section className="px-5 pb-20 lg:px-8" data-testid="pharma-framework-section">
        <div className="mx-auto max-w-7xl rounded-[2rem] bg-slate-900 p-8 text-white md:p-12">
          <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-teal-300">Cadre</p>
          <h2 className="mt-4 max-w-3xl font-display text-3xl font-bold leading-tight sm:text-4xl" data-testid="pharma-framework-title">La donnée est une conséquence du parcours, jamais une condition du soin.</h2>
          <ul className="mt-8 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {framework.map((item) => (
              <li key={item} className="rounded-xl border border-white/10 bg-white/[0.06] px-4 py-3 text-sm font-bold text-slate-100" data-testid={`pharma-framework-item-${item.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").slice(0, 40)}`}>{item}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="px-5 pb-20 lg:px-8" data-testid="pharma-methodology-section">
        <div className="mx-auto max-w-7xl rounded-[2rem] border border-emerald-100 bg-white p-8 md:p-12">
          <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-teal-500">Méthodologie</p>
          <h2 className="mt-4 max-w-4xl font-display text-3xl font-bold leading-tight text-slate-800 sm:text-4xl">Une donnée issue du suivi coordonné, pas d’un questionnaire isolé.</h2>
          <div className="mt-10 grid gap-4 md:grid-cols-4">
            {["Parcours coordonné", "Consentement et pseudonymisation forte", "Structuration de la cohorte", "Rapport agrégé"].map((step, index) => (
              <div key={step} className="rounded-2xl bg-emerald-50 p-5 text-center" data-testid={`pharma-method-step-${index + 1}`}>
                <span className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-emerald-600 text-sm font-extrabold text-white">{index + 1}</span>
                <p className="mt-4 text-sm font-extrabold text-slate-800">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-5 pb-20 lg:px-8" data-testid="pharma-deliverables-section">
        <div className="mx-auto max-w-7xl">
          <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-teal-500">Livrables</p>
          <h2 className="mt-4 max-w-3xl font-display text-3xl font-bold leading-tight text-slate-800 sm:text-4xl">Ce qu’un partenaire reçoit concrètement.</h2>
          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {[
              ["Tableau de bord agrégé", "Observance, symptômes rapportés, toxicités, événements du parcours."],
              ["Rapport descriptif", "Par indication, protocole, ligne de traitement ou molécule."],
              ["Export encadré", "Fichiers JSON/CSV pseudonymisés, avec dictionnaire des données."],
              ["Comité de suivi", "Revue des indicateurs et de leurs limites méthodologiques."],
              ["Cohortes ciblées", "Type de cancer, biomarqueurs, traitement, contexte clinique."],
              ["Cadre de sécurité", "Traçabilité des accès, protection des petits effectifs, finalités définies."],
            ].map(([title, text]) => (
              <article key={title} className="rounded-3xl border border-emerald-100 bg-white p-6 shadow-sm" data-testid={`pharma-deliverable-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <h3 className="text-xl font-extrabold text-slate-800">{title}</h3>
                <p className="mt-3 text-sm leading-6 text-slate-500">{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function AudienceFormSection({ audience }) {
  if (audience === "patients") {
    return (
      <section id="patient-activation-section" className="scroll-mt-20 px-5 pb-20 lg:px-8" data-testid="audience-patient-form-section">
        <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.82fr_1.18fr] lg:items-start">
          <div>
            <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-teal-500">Commencer avec HANA</p>
            <h2 className="mt-4 font-display text-3xl font-bold leading-tight text-slate-800 sm:text-4xl" data-testid="patient-signup-title">Votre accès est ouvert par l’équipe qui vous soigne.</h2>
            <p className="mt-5 text-lg leading-8 text-slate-500" data-testid="patient-signup-description">Indiquez votre clinique et votre médecin. Si votre établissement utilise déjà HANA, l’activation se fait avec votre équipe de soin. Sinon, votre demande nous aide à savoir quelles structures accompagner en priorité.</p>
            <div className="mt-8 rounded-2xl bg-emerald-50 p-5 text-sm font-semibold leading-6 text-emerald-800" data-testid="patient-signup-note">HANA s’utilise toujours avec une équipe médicale. Le service ne remplace pas une consultation ni les consignes données par votre médecin.</div>
          </div>
          <PatientInterestForm />
        </div>
      </section>
    );
  }
  if (audience === "professionnels") {
    return (
      <section id="professional-contact-section" className="scroll-mt-20 px-5 pb-20 lg:px-8" data-testid="audience-professional-form-section">
        <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.82fr_1.18fr] lg:items-start">
          <div>
            <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-teal-500">Démonstration</p>
            <h2 className="mt-4 font-display text-3xl font-bold leading-tight text-slate-800 sm:text-4xl">Voyez HANA sur les parcours de votre établissement.</h2>
            <p className="mt-5 text-lg leading-8 text-slate-500">Trente minutes avec votre planning de cures, vos protocoles et vos règles d’alerte. Sans engagement, sans branchement informatique.</p>
          </div>
          <ProfessionalContactForm />
        </div>
      </section>
    );
  }
  return (
    <section id="pharma-contact-section" className="scroll-mt-20 px-5 pb-20 lg:px-8" data-testid="audience-pharma-form-section">
      <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.82fr_1.18fr] lg:items-start">
        <div>
          <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-teal-500">Échange confidentiel</p>
          <h2 className="mt-4 font-display text-3xl font-bold leading-tight text-slate-800 sm:text-4xl">Cadrer un projet de données ou de soutien aux patients.</h2>
          <p className="mt-5 text-lg leading-8 text-slate-500">Observance, toxicités, symptômes, biomarqueurs, cohortes : nous définissons ensemble les indicateurs utiles et le cadre de gouvernance.</p>
        </div>
        <PharmaContactForm />
      </div>
    </section>
  );
}

const ROUTE_BY_AUDIENCE = { patients: "/patients", professionnels: "/professionnels", pharma: "/pharma" };

function AudienceContent({ audience, config }) {
  useSeo(config.seoTitle, config.seoDescription);
  useHashScroll();
  return (
    <main className="min-h-screen bg-white font-sans text-slate-800" data-testid={`audience-page-${audience}`}>
      <PublicHeader current={ROUTE_BY_AUDIENCE[audience]} />

      <section className="px-5 py-20 lg:px-8 lg:py-28" data-testid="audience-page-hero-section">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[1fr_0.85fr] lg:items-center">
          <div>
            <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-teal-500" data-testid="audience-page-badge">{config.heroBadge}</p>
            <h1 className="mt-5 font-display text-4xl font-bold leading-tight tracking-tight text-slate-800 sm:text-5xl lg:text-6xl" data-testid="audience-page-title">{config.title}</h1>
            <p className="mt-6 max-w-3xl text-lg leading-8 text-slate-500" data-testid="audience-page-description">{config.description}</p>
            {config.note && <p className="mt-5 max-w-3xl rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-sm font-semibold leading-6 text-emerald-800" data-testid="audience-page-note">{config.note}</p>}
            <div className="mt-9 flex flex-col gap-3 sm:flex-row" data-testid="audience-page-cta-row">
              <SmartLink to={config.ctaTo} className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-6 py-3 text-center text-sm font-bold leading-5 text-white transition-all hover:-translate-y-0.5 hover:bg-emerald-700" testId="audience-page-primary-cta">
                <span>{config.primaryCta}</span>
                <ArrowRight className="h-4 w-4 shrink-0" />
              </SmartLink>
              <SmartLink to={config.secondaryTo} className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-100 bg-white px-6 py-3 text-sm font-bold text-emerald-700 transition-all hover:-translate-y-0.5 hover:bg-emerald-50" testId="audience-page-secondary-cta">{config.secondaryCta}</SmartLink>
            </div>
          </div>
          <AbstractJourney flow={config.flow} />
        </div>
      </section>

      {audience === "professionnels" && <ValueLevelsCompact />}

      <section className="px-5 pb-20 lg:px-8 lg:pb-28" data-testid="audience-page-highlights-section">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4" data-testid="audience-page-highlights-grid">
            {config.highlights.map(([Icon, title, text]) => (
              <article key={title} className="rounded-3xl border border-emerald-100 bg-white p-6 shadow-sm" data-testid={`audience-highlight-${title.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-")}`}>
                <Icon className="h-7 w-7 text-teal-500" />
                <h2 className="mt-5 text-xl font-extrabold text-slate-800">{title}</h2>
                <p className="mt-3 text-sm leading-6 text-slate-500">{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {audience === "professionnels" && <ProfessionalDepthSections />}
      {audience === "professionnels" && <ReferencesSection />}
      {audience === "pharma" && <PharmaDepthSections />}

      <AudienceFormSection audience={audience} />

      <section className="px-5 pb-20 lg:px-8" data-testid="audience-page-closing-section">
        <div className="mx-auto max-w-7xl rounded-[2rem] bg-emerald-600 p-8 text-white md:p-12">
          <p className="max-w-4xl font-display text-3xl font-bold leading-tight md:text-4xl" data-testid="audience-page-closing-text">{config.closing}</p>
        </div>
      </section>
      <PublicFooter />
      <ScrollToTopButton />
    </main>
  );
}

export default function HanaAudiencePage({ audience: audienceProp }) {
  const { audience: audienceParam } = useParams();
  const audience = audienceProp || audienceParam;
  const config = pageConfig[audience];
  if (!config) return <Navigate to="/" replace />;
  return <AudienceContent audience={audience} config={config} />;
}
